import * as React from 'react'
import { Progress as ProgressPrimitive } from '@base-ui/react/progress'

import { cn } from '@/registry/reapit/lib/utils'

/*
 * Progress — migrated from Radix to Base UI
 *
 * Base UI structure: Root > Track > Indicator
 *   - Root takes `value: number | null` (null = indeterminate)
 *   - Root renders role="progressbar" with correct aria attributes
 *   - Track is the visual rail container
 *   - Indicator auto-sizes itself via inline styles
 *
 * DS alignment:
 *   Track colour: --muted (neutral-050 = #F2F4F6)
 *   Fill colour:  bg-primary (purple-500 = #4E56EA)
 *   Rounded:      rounded-full — DS specifies pill-shaped track
 *   Sizes:        default = h-2 (8px); small = h-0.5 (2px, DS thin variant)
 *   Indeterminate: animated via @keyframes progress-indeterminate in globals.css
 */

type ProgressProps = Omit<React.ComponentProps<typeof ProgressPrimitive.Root>, 'value'> & {
  /** The current progress value (0–100). Omit or pass undefined for indeterminate. */
  value?: number
  /** DS: indeterminate animated progress bar (linear). */
  indeterminate?: boolean
  /** small = 2px (DS thin); default = 8px. */
  size?: 'small' | 'default'
}

function Progress({ className, value, indeterminate = false, size = 'default', ...props }: ProgressProps) {
  // Base UI: null = indeterminate, number = determinate
  const progressValue = indeterminate ? null : (value ?? null)

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      data-size={size}
      data-indeterminate={indeterminate || undefined}
      value={progressValue}
      {...props}
    >
      <ProgressPrimitive.Track
        className={cn(
          'relative w-full overflow-hidden rounded-full bg-muted',
          size === 'small' ? 'h-0.5' : 'h-2',
          className,
        )}
      >
        {indeterminate ? (
          <div
            data-slot="progress-indicator"
            className="h-full w-1/3 bg-primary animate-[progress-indeterminate_1.5s_ease-in-out_infinite]"
          />
        ) : (
          <ProgressPrimitive.Indicator
            data-slot="progress-indicator"
            className="h-full w-full flex-1 bg-primary transition-all"
            style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
          />
        )}
      </ProgressPrimitive.Track>
    </ProgressPrimitive.Root>
  )
}

export { Progress }
